import { useState } from "react";
import { toast } from "sonner";
import { Trash2, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import Modal from "@/components/Modal";
import { Field, SectionTitle, Spinner } from "@/components/common";
import { useLoad, useTrip } from "@/context/TripContext";
import { api, errMsg } from "@/lib/api";

const ACTS = ["beach", "hiking", "formal", "nightlife", "wedding"];

function Template({ kind, onDone }) {
  const { tid } = useTrip();
  const [f, setF] = useState({ climate: "warm", activities: [] });
  const go = async () => { try { const { data } = await api.post(`/trips/${tid}/lists/template`, { kind, ...f }); toast.success(`Added ${data.added} items`); onDone(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-3">
      {kind === "personal" && <>
        <Field label="Weather" htmlFor="tc"><select id="tc" className="nselect" value={f.climate} onChange={(e) => setF({ ...f, climate: e.target.value })} data-testid="template-climate"><option value="warm">Warm</option><option value="cold">Cold</option><option value="mixed">Mixed / rainy</option></select></Field>
        <div className="flex flex-wrap gap-2">{ACTS.map((a) => <button type="button" key={a} onClick={() => setF({ ...f, activities: f.activities.includes(a) ? f.activities.filter((x) => x !== a) : [...f.activities, a] })} className={`pill capitalize ${f.activities.includes(a) ? "bg-orange-500 text-white" : "bg-slate-100"}`} data-testid={`template-act-${a}`}>{a}</button>)}</div>
        <p className="text-xs text-slate-500">Uses the trip length for outfit counts. Edit freely afterwards.</p></>}
      <Button className="w-full rounded-full" onClick={go} data-testid="template-generate">Add suggested items</Button>
    </div>
  );
}

function Section({ title, kind, items, reload, canAdd, my, sub }) {
  const { tid, isOrg } = useTrip();
  const [text, setText] = useState("");
  const [tpl, setTpl] = useState(false);
  const act = async (fn) => { try { await fn(); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const add = (e) => { e.preventDefault(); if (text.trim()) act(() => api.post(`/trips/${tid}/lists`, { kind, text }).then(() => setText(""))); };
  const done = items.filter((i) => i.done).length;
  return (
    <div className="card p-5" data-testid={`list-section-${kind}`}>
      <div className="flex items-center justify-between"><h3 className="font-bold">{title}</h3><span className="font-mono text-xs text-slate-500">{done}/{items.length}</span></div>
      {sub && <p className="text-xs text-slate-500">{sub}</p>}
      <ul className="mt-3 space-y-1.5">
        {items.map((i) => (
          <li key={i.list_item_id} className="flex items-center gap-2 text-sm" data-testid={`list-item-${i.list_item_id}`}>
            <Checkbox checked={i.done} disabled={kind === "task" && !isOrg} onCheckedChange={(v) => act(() => api.patch(`/trips/${tid}/lists/${i.list_item_id}`, { done: !!v }))} data-testid={`list-check-${i.list_item_id}`} />
            <span className={`flex-1 ${i.done ? "text-slate-400 line-through" : ""}`}>{i.text}</span>
            {kind === "shared" && (i.claimed_by ? <button className={`pill ${i.claimed_by === my ? "bg-teal-100 text-teal-800" : "bg-slate-100 text-slate-600"}`} onClick={() => (i.claimed_by === my || isOrg) && act(() => api.patch(`/trips/${tid}/lists/${i.list_item_id}`, { claim: false }))} data-testid={`list-claimed-${i.list_item_id}`}>{i.claimed_by === my ? "You're bringing it" : i.claimed_by_name}</button>
              : <Button size="sm" variant="outline" className="h-7 rounded-full" onClick={() => act(() => api.patch(`/trips/${tid}/lists/${i.list_item_id}`, { claim: true }))} data-testid={`list-claim-${i.list_item_id}`}>I'll bring it</Button>)}
            {(kind === "personal" || isOrg || i.created_by === my) && <button onClick={() => act(() => api.delete(`/trips/${tid}/lists/${i.list_item_id}`))} aria-label="Delete" data-testid={`list-delete-${i.list_item_id}`}><Trash2 className="h-3.5 w-3.5 text-slate-400 hover:text-rose-500" /></button>}
          </li>))}
        {items.length === 0 && <li className="text-sm text-slate-400">Nothing here yet.</li>}
      </ul>
      {canAdd && <form onSubmit={add} className="mt-3 flex gap-2"><Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Add an item" data-testid={`list-input-${kind}`} /><Button type="submit" variant="outline" data-testid={`list-add-${kind}`}>Add</Button></form>}
      {kind !== "task" && <Button size="sm" variant="ghost" className="mt-2 rounded-full" onClick={() => setTpl(true)} data-testid={`list-template-${kind}`}><Wand2 className="mr-1 h-4 w-4" />Suggest items</Button>}
      <Modal open={tpl} onOpenChange={setTpl} title="Suggested items" testid="template-modal"><Template kind={kind} onDone={() => { setTpl(false); reload(); }} /></Modal>
    </div>
  );
}

export default function Lists() {
  const { tid, isOrg } = useTrip();
  const [d, reload] = useLoad(`/trips/${tid}/lists`);
  if (!d) return <Spinner />;
  const by = (k) => d.items.filter((i) => i.kind === k);
  return (
    <div className="space-y-5">
      <SectionTitle title="Packing & planning lists" />
      <div className="grid gap-5 lg:grid-cols-3">
        <Section title="My packing list" kind="personal" sub="Only you can see this." items={by("personal")} reload={reload} canAdd my={d.my_member_id} />
        <Section title="Shared supplies" kind="shared" sub="Claim what you'll bring." items={by("shared")} reload={reload} canAdd my={d.my_member_id} />
        <Section title="Planning tasks" kind="task" sub="Organizer to-dos — count toward readiness." items={by("task")} reload={reload} canAdd={isOrg} my={d.my_member_id} />
      </div>
    </div>
  );
}
